import { failure, isOk, ok, pending, Result } from "../common/common";
import { SuggestedCompany } from "../types/CompanyInfo";
import { OnboardingCandidateResponse } from "../stores/PortfolioStore";
import { makeAutoObservable } from "mobx";
import { TMessageKey } from "../i18n/types.gen";
import { TMessage } from "../i18n/tt";
import { createEmailField, minLengthValidator, StringFieldModel } from "../common/commonForms";

export class CompanySearchController {
  searchResult: Result<SuggestedCompany[]> | undefined;
  searchField: StringFieldModel;
  selectedCompany?: SuggestedCompany;
  onSearchBolagsverket: (query: string) => Promise<SuggestedCompany[]>;
  onSearchCompanies: (query: string) => Promise<SuggestedCompany[]>;
  onSendAskForPermissionRequest: (
    email: string,
    organisationNumber: string,
    companyName: string,
  ) => Promise<void>;
  onClose: (reload: boolean) => void;
  onFindOnboardingCandidates: (organisationNumber: string) => Promise<OnboardingCandidateResponse>;
  onboardingCandidatesResult?: Result<OnboardingCandidateResponse>;
  askForPermissionResult?: Result<TMessage, TMessage>;
  receiverEmailAddress: StringFieldModel = createEmailField({ required: true });
  companyNameSearchField: StringFieldModel;

  constructor(
    onClose: (reload: boolean) => void,
    onSearchBolagsverket: (query: string) => Promise<SuggestedCompany[]>,
    onSearchCompanies: (query: string) => Promise<SuggestedCompany[]>,
    onSendAskForPermissionRequest: (
      organisationNumber: string,
      companyName: string,
      email: string,
    ) => Promise<void>,
    onFindOnboardingCandidates: (
      organisationNumber: string,
    ) => Promise<OnboardingCandidateResponse>,
  ) {
    this.onSearchBolagsverket = onSearchBolagsverket;
    this.onSearchCompanies = onSearchCompanies;
    this.onSendAskForPermissionRequest = onSendAskForPermissionRequest;
    this.onFindOnboardingCandidates = onFindOnboardingCandidates;
    this.searchField = new StringFieldModel({
      required: true,
      validator: minLengthValidator(3),
    });
    this.onClose = onClose;
    this.companyNameSearchField = new StringFieldModel();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  *searchSwedishBusinessRegister() {
    this.searchField.validate();
    if (this.searchField.isValid) {
      this.searchResult = pending();
      try {
        const result: SuggestedCompany[] = yield this.onSearchBolagsverket(
          this.searchField.validatedValue,
        );
        this.searchResult = ok(result);
      } catch (e) {
        this.searchResult = failure();
      }
    }
  }

  *searchForCompany(query: string) {
    this.companyNameSearchField.setInputValue(query);
    if (this.companyNameSearchField.inputValue.length > 2) {
      this.searchResult = pending();
      try {
        const result: SuggestedCompany[] = yield this.onSearchCompanies(
          this.companyNameSearchField.inputValue,
        );
        this.searchResult = ok(result);
      } catch (e) {
        this.searchResult = failure();
      }
    }
  }

  *onSubmit() {
    if (this.selectedCompany) {
      this.receiverEmailAddress.validate();
      if (this.receiverEmailAddress.isValid) {
        try {
          this.askForPermissionResult = pending();
          yield this.onSendAskForPermissionRequest(
            this.selectedCompany.organizationnumber,
            this.selectedCompany.name,
            this.receiverEmailAddress.inputValue,
          );
          this.askForPermissionResult = ok({ key: "general-success" });
        } catch (e) {
          this.askForPermissionResult = failure({ key: "askForPermissionDialog.error" });
        }
      }
    }
  }

  get companyRegisterKey(): TMessageKey {
    if (this.selectedCompany?.sourceCountry === "SE") {
      return "askForPermissionDialog.bolagsverket";
    } else {
      return "askForPermissionDialog.brreg";
    }
  }

  *getOnboardingCandidates() {
    if (this.selectedCompany) {
      try {
        this.onboardingCandidatesResult = pending();
        const response: OnboardingCandidateResponse = yield this.onFindOnboardingCandidates(
          this.selectedCompany?.organizationnumber,
        );
        this.onboardingCandidatesResult = ok(response);
      } catch (e) {
        this.onboardingCandidatesResult = failure();
      }
    }
  }

  get isRenderSearchResult(): boolean {
    return (
      isOk(this.searchResult) &&
      this.searchResult.data.length > 0 &&
      this.searchResult.data.length <= maxCompanySearchHits
    );
  }

  get tooManyHits(): boolean {
    return isOk(this.searchResult) && this.searchResult.data.length > maxCompanySearchHits;
  }

  selectSwedishCompany(company: SuggestedCompany) {
    this.searchResult = undefined;
    this.selectedCompany = company;
    this.getOnboardingCandidates();
  }

  selectCompany(company: SuggestedCompany | null) {
    if (company) {
      this.selectedCompany = company;
      this.getOnboardingCandidates();
      this.companyNameSearchField.setInputValue(company.name);
    }
  }

  get getSearchResult(): SuggestedCompany[] {
    if (isOk(this.searchResult)) {
      return this.searchResult.data;
    }
    return [];
  }
}

const maxCompanySearchHits = 50;
